import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { useNetWorth, shortINR } from "@/context/NetWorthContext";
import { branchTotal } from "@/lib/tree";
import { Avatar } from "@/components/PersonCard";
import { t } from "@/lib/i18n";

const MEDAL = ["#C59B27", "#9AA5A0", "#B87333"];

const useLines = (persons, tree, map, visible) => useMemo(() => {
  if (!visible || !persons?.length) return [];
  const seen = new Set();
  const lines = [];
  persons.forEach((p) => {
    const couple = [p, ...tree.spousesOf(p.person_id)];
    if (!couple.some((c) => tree.childrenOf(c.person_id).length)) return;
    const key = couple.map((c) => c.person_id).sort().join("|");
    if (seen.has(key)) return;
    seen.add(key);
    lines.push({ head: p, partners: couple.slice(1), ...branchTotal(p.person_id, tree, map) });
  });
  return lines.sort((a, b) => b.total - a.total || b.members - a.members);
}, [persons, tree, map, visible]);

const LineRow = ({ l, i }) => (
  <Link to={`/person/${l.head.person_id}`} data-testid={`leaderboard-row-${l.head.person_id}`}
    className="min-w-0 w-full flex items-center gap-3 sm:gap-4 rounded-2xl border border-[#E5DEC9] bg-white px-4 py-3 hover:border-[#1E3A2B] hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
    <span className="w-8 h-8 shrink-0 rounded-full grid place-items-center font-mono text-sm font-bold"
      style={{ background: i < 3 ? `${MEDAL[i]}26` : "#F4EDE2", color: i < 3 ? MEDAL[i] : "#7E8E85" }} data-testid={`leaderboard-rank-${l.head.person_id}`}>{i + 1}</span>
    <Avatar p={l.head} size="w-10 h-10 text-sm shrink-0" />
    <span className="flex-1 min-w-0">
      <span className="block font-serif font-semibold truncate">{l.head.name}{l.partners.length ? ` & ${l.partners.map((s) => s.name).join(", ")}` : ""}</span>
      <span className="block text-xs text-[#7E8E85]">{l.counted}/{l.members} {t("with net worth added")}</span>
    </span>
    <span className="shrink-0 font-serif text-base sm:text-lg font-bold text-[#1E3A2B]" data-testid={`leaderboard-total-${l.head.person_id}`}>{shortINR(l.total) || "₹0"}</span>
  </Link>
);

export const LineageLeaderboard = ({ persons, tree }) => {
  const { map, visible } = useNetWorth();
  const [all, setAll] = useState(false);
  const lines = useLines(persons, tree, map, visible);
  if (!lines.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-10" data-testid="lineage-leaderboard">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-2 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" />{t("Lineage leaderboard")}</p>
      <h2 className="font-serif text-2xl sm:text-3xl font-semibold mb-6">{t("Family lines ranked by combined net worth")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(all ? lines : lines.slice(0, 10)).map((l, i) => <LineRow key={l.head.person_id} l={l} i={i} />)}
      </div>
      {lines.length > 10 && (
        <button onClick={() => setAll(!all)} className="mt-4 text-sm font-medium text-[#D96B43]" data-testid="leaderboard-toggle-btn">
          {all ? t("Show top 10") : `${t("Show all")} (${lines.length})`}
        </button>
      )}
    </section>
  );
};
